import { Close as CloseIcon, Groups as GroupsIcon, Person as PersonIcon } from "@mui/icons-material";
import { Box, Drawer, IconButton, List, ListItem, Stack, Typography } from "@mui/material";

import { SearchField } from "~ui/atoms/SearchField/SearchField";
import { AddMemberDialog } from "~ui/molecules/AddMemberDialog/AddMemberDialog";
import { MemberCard } from "~ui/molecules/MemberCard/MemberCard";
import { type Member } from "~ui/molecules/MemberCard/types";

import { useMembersSlideout } from "./useMembersSlideout";

// Centralized strings for internationalization
const tAddFirstMember = "Add your first member to get started";
const tFacilitators = "⭐ Facilitators";
const tMembers = "👥 Members";
const tMembersHeader = "Members";
const tMembersWillAppear = "Members will appear here when they join";
const tNoMembers = "No members yet";
const tNoMembersFound = "No members found";
const tPendingRequests = "📋 Pending Requests";
const tSearchMembers = "Search members...";
const tTryDifferentSearch = "Try searching with different keywords";

interface MembersSlideoutProps {
  open: boolean;
  onClose: () => void;
  members: Member[];
  requests: Member[];
  canManage?: boolean;
  canSeeStatus?: boolean;
  isOwner?: boolean;
  onAddMember?: (member: { firstName: string; lastName: string; email: string }) => void;
  onRemoveMember?: (memberId: string) => void;
  onApproveMember?: (memberId: string) => void;
  onRejectMember?: (memberId: string) => void;
  onAddFacilitator?: (memberId: string) => void;
  onRemoveFacilitator?: (memberId: string) => void;
  onAddOwner?: (memberId: string) => void;
}

export const MembersSlideout = ({
  open,
  onClose,
  members,
  requests,
  canManage = false,
  canSeeStatus = false,
  isOwner = false,
  onAddMember,
  onRemoveMember,
  onApproveMember,
  onRejectMember,
  onAddFacilitator,
  onRemoveFacilitator,
  onAddOwner,
}: MembersSlideoutProps) => {
  const { facilitators, filteredMembers, filteredRequests, regularMembers, searchQuery, setSearchQuery, totalMembers } =
    useMembersSlideout(members, requests);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          borderRadius: "16px 0 0 16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          width: { sm: 400, xs: "100%" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            alignItems: "center",
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            p: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <GroupsIcon sx={{ fontSize: "2rem" }} />
            <Box>
              <Typography variant="h2" component="h2" sx={{ fontSize: "1.25rem", fontWeight: "bold" }}>
                {tMembersHeader}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {totalMembers} {totalMembers === 1 ? "member" : "members"}
                {requests.length > 0 && ` (${requests.length} pending)`}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            sx={{
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Add Member Button */}
        {canManage && onAddMember && (
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", p: 2 }}>
            <AddMemberDialog onAddMember={onAddMember} />
          </Box>
        )}

        {/* Search Field */}
        <Box sx={{ borderBottom: "1px solid", borderColor: "divider", p: 2 }}>
          <SearchField value={searchQuery} onChange={setSearchQuery} placeholder={tSearchMembers} size="small" />
        </Box>

        {/* Members List */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {/* Pending Requests Section */}
          {filteredRequests.length > 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h3" component="h3" sx={{ fontSize: "1.125rem", fontWeight: "bold" }} gutterBottom>
                {tPendingRequests} ({filteredRequests.length}
                {searchQuery ? ` of ${requests.length}` : ""})
              </Typography>
              <List sx={{ p: 0 }}>
                {filteredRequests.map((request) => (
                  <ListItem key={request.id} sx={{ p: 0 }}>
                    <MemberCard
                      member={request}
                      canManage={canManage}
                      canSeeStatus={canSeeStatus}
                      isOwner={isOwner}
                      showPendingActions={true}
                      onApprove={onApproveMember}
                      onReject={onRejectMember}
                      onAddFacilitator={onAddFacilitator}
                      onRemoveFacilitator={onRemoveFacilitator}
                      onAddOwner={onAddOwner}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Facilitators Section */}
          {facilitators.length > 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h3" component="h3" sx={{ fontSize: "1.125rem", fontWeight: "bold" }} gutterBottom>
                {tFacilitators} ({facilitators.length}
                {searchQuery ? ` of ${members.filter((m) => m.isFacilitator).length}` : ""})
              </Typography>
              <List sx={{ p: 0 }} data-testid="member-list">
                {facilitators.map((facilitator) => (
                  <ListItem key={facilitator.id} sx={{ p: 0 }}>
                    <MemberCard
                      member={facilitator}
                      canManage={canManage}
                      canSeeStatus={canSeeStatus}
                      isOwner={isOwner}
                      onRemove={onRemoveMember}
                      onAddFacilitator={onAddFacilitator}
                      onRemoveFacilitator={onRemoveFacilitator}
                      onAddOwner={onAddOwner}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Regular Members Section */}
          {regularMembers.length > 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h3" component="h3" sx={{ fontSize: "1.125rem", fontWeight: "bold" }} gutterBottom>
                {tMembers} ({regularMembers.length}
                {searchQuery ? ` of ${members.filter((m) => !m.isFacilitator).length}` : ""})
              </Typography>
              <List sx={{ p: 0 }} data-testid="member-list">
                {regularMembers.map((member) => (
                  <ListItem key={member.id} sx={{ p: 0 }}>
                    <MemberCard
                      member={member}
                      canManage={canManage}
                      canSeeStatus={canSeeStatus}
                      isOwner={isOwner}
                      onRemove={onRemoveMember}
                      onAddFacilitator={onAddFacilitator}
                      onRemoveFacilitator={onRemoveFacilitator}
                      onAddOwner={onAddOwner}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* No Search Results State */}
          {searchQuery && filteredMembers.length === 0 && filteredRequests.length === 0 && (
            <Box
              sx={{
                alignItems: "center",
                color: "text.secondary",
                display: "flex",
                flexDirection: "column",
                height: "200px",
                justifyContent: "center",
              }}
            >
              <PersonIcon sx={{ fontSize: "4rem", mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                {tNoMembersFound}
              </Typography>
              <Typography variant="body2" textAlign="center">
                {tTryDifferentSearch}
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {!searchQuery && members.length === 0 && requests.length === 0 && (
            <Box
              sx={{
                alignItems: "center",
                color: "text.secondary",
                display: "flex",
                flexDirection: "column",
                height: "200px",
                justifyContent: "center",
              }}
            >
              <PersonIcon sx={{ fontSize: "4rem", mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                {tNoMembers}
              </Typography>
              <Typography variant="body2" textAlign="center">
                {canManage ? tAddFirstMember : tMembersWillAppear}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
